// Gerencia o infowindow flutuante de veículos no mapa de monitoramento
import { MODAL_ICONS } from "./monitoring/modal_icons"

export class VehicleBalloonManager {
  constructor(mapController) {
    this.mapController = mapController
    this.currentBalloon = null
    this.currentInfoWindow = null
    this.currentClickHandler = null
    this.currentVehicleId = null
    this.isProcessingClick = false
    this.deviceCache = {}
  }

  t(key) {
    return window.I18n?.translations?.[key] || key
  }

  showBalloon(vehicle, marker) {
    if (this.isProcessingClick) return
    this.isProcessingClick = true

    try {
      this.closeAllBalloons()
      const balloon = this.createBalloonElement(vehicle)
      this.currentBalloon = balloon
      this.currentVehicleId = vehicle.id

      this.currentInfoWindow = new google.maps.InfoWindow({
        content: balloon,
        disableAutoPan: false,
        pixelOffset: new google.maps.Size(0, -8)
      })

      this.currentInfoWindow.open({
        anchor: marker,
        map: this.mapController.map
      })

      this.currentInfoWindow.addListener('closeclick', () => this.closeAllBalloons())

      this.loadDevices(vehicle, balloon)
    } finally {
      setTimeout(() => { this.isProcessingClick = false }, 200)
    }
  }

  // Redesenha o balão aberto com os dados do ciclo de refresh, sem fechar e
  // reabrir o InfoWindow — reabrir roubaria o foco e faria o mapa re-panar.
  // O marker é o mesmo objeto entre refreshes (só a posição muda), então a
  // ancoragem continua válida sem reabrir.
  refreshBalloon(vehicle) {
    const balloon = this.currentBalloon
    if (!balloon || this.currentVehicleId !== vehicle.id) return

    const activeTab = this.activeTabIndex(balloon)
    balloon.innerHTML = this.generateHeaderHTML(vehicle) + this.generateSkeletonHTML()
    this.bindCloseButton(balloon)
    this.loadDevices(vehicle, balloon, activeTab)
  }

  activeTabIndex(balloon) {
    const panels = Array.from(balloon.querySelectorAll('.balloon-device-panel'))
    const visible = panels.findIndex(panel => panel.style.display !== 'none')
    return visible === -1 ? 0 : visible
  }

  // Os dados de device vêm de /monitoring/:id/devices.json e envelhecem junto
  // com o mapa — sem isso, reabrir um balão depois do refresh mostrava a
  // leitura da primeira abertura para sempre.
  clearDeviceCache() {
    this.deviceCache = {}
  }

  createBalloonElement(vehicle) {
    const balloon = document.createElement('div')
    balloon.className = 'vehicle-info-balloon'
    balloon.style.cssText = 'width:340px;background:var(--mono-bg);border-radius:18px;overflow:hidden;'
    balloon.innerHTML = this.generateHeaderHTML(vehicle) + this.generateSkeletonHTML()
    this.bindCloseButton(balloon)
    return balloon
  }

  generateHeaderHTML(vehicle) {
    const statusData = this.getVehicleStatusData(vehicle)
    return `
      <div style="padding: 14px 16px; border-bottom: 1px solid var(--mono-border);">
        <button class="close-balloon-btn" style="
          position: absolute; top: 10px; right: 10px;
          width: 26px; height: 26px; background: var(--mono-card-bg);
          border: none; border-radius: 50%; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          color: var(--mono-text-dim);
        ">
          <svg width="12" height="12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <div style="display: flex; align-items: center; gap: 10px; padding-right: 30px;">
          <div style="
            width: 38px; height: 38px; border-radius: 12px; flex-shrink: 0;
            background: var(--mono-card-bg); display: flex; align-items: center; justify-content: center;
          ">
            <svg width="20" height="20" style="fill: var(--mono-text-dim);" viewBox="0 0 24 24">
              ${MODAL_ICONS[vehicle.vehicle_modal] || MODAL_ICONS.road}
            </svg>
          </div>
          <div style="min-width: 0; flex: 1;">
            <div style="font-size: 14px; font-weight: 700; color: var(--mono-text); margin-bottom: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              ${vehicle.plate}
            </div>
            <div style="font-size: 11px; color: var(--mono-text-faint); margin-bottom: 5px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">
              ${this.formatDriverName(vehicle.driver)}
            </div>
            <div style="display: flex; gap: 6px; flex-wrap: wrap;">
              ${statusData.ignitionBadge}
              ${statusData.commBadge}
            </div>
          </div>
        </div>

        <div style="display: flex; align-items: center; gap: 4px; margin-top: 8px;">
          <svg width="10" height="10" fill="none" style="stroke: var(--mono-text-faint);" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <span style="font-size: 10px; color: var(--mono-text-faint);">${vehicle.last_communication || '—'}</span>
        </div>
      </div>
    `
  }

  generateSkeletonHTML() {
    return `
      <div class="balloon-body" style="padding: 12px 16px; max-height: 360px; overflow-y: auto;">
        <div class="balloon-skeleton">
          ${[1, 2, 3].map(() => `
            <div style="display: flex; gap: 10px; margin-bottom: 10px;">
              <div style="width: 32px; height: 32px; background: var(--mono-card-bg); border-radius: 10px; flex-shrink: 0;"></div>
              <div style="flex: 1;">
                <div style="height: 12px; background: var(--mono-card-bg); border-radius: 6px; margin-bottom: 5px; width: 60%;"></div>
                <div style="height: 10px; background: var(--mono-card-bg); border-radius: 6px; width: 40%;"></div>
              </div>
            </div>
          `).join('')}
        </div>
      </div>
    `
  }

  loadDevices(vehicle, balloon, activeTab = 0) {
    const vehicleId = vehicle.id

    if (this.deviceCache[vehicleId]) {
      this.renderDevices(balloon, vehicle, this.deviceCache[vehicleId], activeTab)
      return
    }

    const url = `/monitoring/${vehicleId}/devices.json`

    fetch(url, { headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.json())
      .then(data => {
        this.deviceCache[vehicleId] = data.devices
        if (this.currentBalloon === balloon) {
          this.renderDevices(balloon, vehicle, data.devices, activeTab)
        }
      })
      .catch(() => {
        if (this.currentBalloon === balloon) {
          this.renderDevicesError(balloon, vehicle)
        }
      })
  }

  renderDevices(balloon, vehicle, devices, activeTab = 0) {
    const body = balloon.querySelector('.balloon-body')
    if (!body) return

    const validDevices = (devices || []).filter(d => d)
    const showTabs = validDevices.length > 1
    const selected = activeTab < validDevices.length ? activeTab : 0

    let html = ''

    if (showTabs) {
      html += `<div class="balloon-tabs" style="display: flex; border-bottom: 1px solid var(--mono-border); margin-bottom: 12px; overflow-x: auto; gap: 0;">`
      validDevices.forEach((device, i) => {
        const active = i === selected
        html += `
          <button class="balloon-tab" data-tab-index="${i}" style="
            padding: 7px 12px; font-size: 11px; font-weight: ${active ? '600' : '400'};
            color: ${active ? '#60a5fa' : 'var(--mono-text-faint)'}; background: none; border: none;
            border-bottom: 2px solid ${active ? '#60a5fa' : 'transparent'};
            cursor: pointer; white-space: nowrap; flex-shrink: 0; transition: all 0.15s;
          ">${this.escapeHtml(device.label)}</button>
        `
      })
      html += `</div>`
    }

    html += `<div class="balloon-tab-content">`
    validDevices.forEach((device, i) => {
      html += `<div class="balloon-device-panel" data-panel-index="${i}" style="display: ${i === selected ? 'block' : 'none'};">`
      html += this.generateDevicePanel(device)
      html += `</div>`
    })
    html += `</div>`

    body.innerHTML = html
    this.bindTabEvents(balloon)
  }

  renderDevicesError(balloon, vehicle) {
    const body = balloon.querySelector('.balloon-body')
    if (!body) return

    body.innerHTML = `
      <div style="text-align: center; padding: 20px 0; color: var(--mono-text-faint); font-size: 12px;">
        <div style="margin-bottom: 6px; font-size: 20px;">⚠️</div>
        Erro ao carregar dados dos dispositivos
      </div>
      ${this.generateLocationSection(vehicle)}
    `
  }

  generateDevicePanel(device) {
    let html = ''
    const metrics = this.collectMetrics(device)

    if (metrics.length > 0) {
      html += `<div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 14px;">`
      metrics.forEach(m => { html += this.generateMetricCard(m) })
      html += `</div>`
    } else {
      html += `
        <div style="text-align: center; padding: 16px 0; color: var(--mono-text-faint); font-size: 12px; margin-bottom: 12px;">
          ${this.t('telemetry_not_available')}
        </div>
      `
    }

    if (device.has_position && device.lat && device.lng) {
      html += this.generateLocationCard(device.lat, device.lng, device.position?.gps_datetime)
    } else {
      html += `
        <div style="
          padding: 14px; background: var(--mono-card-bg); border-radius: 14px;
          font-size: 11px; color: var(--mono-text-faint); text-align: center;
        ">${this.t('location_not_available')}</div>
      `
    }

    return html
  }

  collectMetrics(device) {
    const tel = device.telemetry || {}
    const pos = device.position || {}
    const metrics = []

    const speed = tel.current_speed ?? (pos.speed != null ? pos.speed : null)
    if (speed != null) metrics.push({ icon: 'speed', color: '#60a5fa', bg: '#1e3a5f', value: `${parseFloat(speed).toFixed(0)}`, unit: 'km/h', label: this.t('speed_label') })

    if (tel.rpm != null) metrics.push({ icon: 'rpm', color: '#a78bfa', bg: '#2d1b69', value: this.formatRpm(tel.rpm), unit: 'rpm', label: this.t('rpm_label') })

    if (tel.engine_temperature != null) {
      const tempColor = tel.engine_temperature >= 80 ? '#f87171' : tel.engine_temperature >= 60 ? '#fb923c' : '#60a5fa'
      metrics.push({ icon: 'temp', color: tempColor, bg: 'var(--mono-card-bg)', value: `${Math.round(tel.engine_temperature)}`, unit: '°C', label: this.t('temp_label') })
    }

    if (tel.engine_oil_pressure != null) metrics.push({ icon: 'oil', color: '#c084fc', bg: '#2d1b69', value: `${parseFloat(tel.engine_oil_pressure).toFixed(1)}`, unit: 'bar', label: this.t('oil_pressure_label') })

    if (tel.hour_meter != null) metrics.push({ icon: 'hour', color: '#4ade80', bg: '#14532d', value: `${parseFloat(tel.hour_meter).toFixed(1)}`, unit: 'h', label: this.t('horimeter_label') })

    return metrics
  }

  generateMetricCard(m) {
    return `
      <div style="
        background: var(--mono-card-bg); border-radius: 14px;
        padding: 10px 12px; display: flex; align-items: center; gap: 10px;
      ">
        <div style="
          width: 32px; height: 32px; border-radius: 10px; background: ${m.bg};
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        ">
          ${this.getMetricSvg(m.icon, m.color)}
        </div>
        <div style="min-width: 0;">
          <div style="font-size: 13px; font-weight: 700; color: ${m.color}; line-height: 1.2;">
            ${this.escapeHtml(m.value)}<span style="font-size: 10px; font-weight: 400; color: var(--mono-text-faint); margin-left: 2px;">${m.unit}</span>
          </div>
          <div style="font-size: 10px; color: var(--mono-text-faint); margin-top: 1px;">${m.label}</div>
        </div>
      </div>
    `
  }

  generateLocationCard(lat, lng, gpsDatetime) {
    const latF = parseFloat(lat).toFixed(6)
    const lngF = parseFloat(lng).toFixed(6)
    const lastComm = this.formatLastCommunication(gpsDatetime)
    const lastCommHtml = lastComm
      ? `<div style="font-size: 10px; color: var(--mono-text-faint); margin-top: 4px;">Última comunicação: ${lastComm}</div>`
      : ''
    return `
      <div style="
        padding: 12px 14px; background: var(--mono-card-bg);
        border-radius: 14px; display: flex; align-items: center; justify-content: space-between;
      ">
        <div>
          <div style="font-size: 10px; color: var(--mono-text-faint); margin-bottom: 2px;">${this.t('gps_coordinates')}</div>
          <div style="font-size: 11px; font-family: monospace; color: var(--mono-text-secondary); user-select: all;">${latF}, ${lngF}</div>
          ${lastCommHtml}
        </div>
        <button class="copy-coords-btn" data-lat="${latF}" data-lng="${lngF}" style="
          width: 30px; height: 30px; background: var(--mono-border-hi); border: none;
          border-radius: 50%; cursor: pointer; display: flex; align-items: center;
          justify-content: center; flex-shrink: 0; transition: background 0.15s;
        ">
          <svg class="copy-icon" width="13" height="13" fill="none" style="stroke: var(--mono-text-dim);" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"/>
          </svg>
          <svg class="check-icon" width="13" height="13" fill="none" stroke="#4ade80" viewBox="0 0 24 24" style="display:none;">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
          </svg>
        </button>
      </div>
    `
  }

  generateLocationSection(vehicle) {
    if (!vehicle.lat || !vehicle.lng) return ''
    return this.generateLocationCard(vehicle.lat, vehicle.lng)
  }

  bindTabEvents(balloon) {
    balloon.querySelectorAll('.balloon-tab').forEach(tab => {
      tab.addEventListener('click', () => {
        const idx = parseInt(tab.dataset.tabIndex)

        balloon.querySelectorAll('.balloon-tab').forEach(t => {
          t.style.color = 'var(--mono-text-faint)'
          t.style.fontWeight = '400'
          t.style.borderBottomColor = 'transparent'
        })
        tab.style.color = '#60a5fa'
        tab.style.fontWeight = '600'
        tab.style.borderBottomColor = '#60a5fa'

        balloon.querySelectorAll('.balloon-device-panel').forEach(p => { p.style.display = 'none' })
        const panel = balloon.querySelector(`.balloon-device-panel[data-panel-index="${idx}"]`)
        if (panel) panel.style.display = 'block'
      })
    })

    balloon.querySelectorAll('.copy-coords-btn').forEach(btn => {
      btn.addEventListener('click', () => this.copyCoordinates(btn))
    })
  }

  bindCloseButton(balloon) {
    const closeBtn = balloon.querySelector('.close-balloon-btn')
    if (closeBtn) closeBtn.addEventListener('click', () => this.closeAllBalloons())
  }

  setupOutsideClickHandler(balloon) {
    if (this.currentClickHandler) {
      document.removeEventListener('click', this.currentClickHandler)
    }

    const clickHandler = (event) => {
      const isInsideBalloon = balloon.contains(event.target)
      const isMapRelated = this.isMapRelatedElement(event.target)
      if (!isInsideBalloon && !isMapRelated) {
        this.closeAllBalloons()
        document.removeEventListener('click', clickHandler)
        this.currentClickHandler = null
      }
    }

    this.currentClickHandler = clickHandler
    setTimeout(() => { document.addEventListener('click', clickHandler) }, 300)
  }

  closeAllBalloons() {
    if (this.currentClickHandler) {
      document.removeEventListener('click', this.currentClickHandler)
      this.currentClickHandler = null
    }
    if (this.currentInfoWindow) {
      this.currentInfoWindow.close()
      this.currentInfoWindow = null
    }
    this.currentBalloon = null
    this.currentVehicleId = null
  }

  copyCoordinates(btn) {
    const lat = btn.dataset.lat
    const lng = btn.dataset.lng
    navigator.clipboard.writeText(`${lat}, ${lng}`).then(() => {
      const copyIcon = btn.querySelector('.copy-icon')
      const checkIcon = btn.querySelector('.check-icon')
      if (copyIcon) copyIcon.style.display = 'none'
      if (checkIcon) checkIcon.style.display = 'block'
      setTimeout(() => {
        if (copyIcon) copyIcon.style.display = 'block'
        if (checkIcon) checkIcon.style.display = 'none'
      }, 1500)
    }).catch(() => {})
  }

  getVehicleStatusData(vehicle) {
    const state = vehicle.vehicle_state?.toLowerCase()
    let ignitionText, ignitionColor
    if (state === 'on') { ignitionText = this.t('ignition_on'); ignitionColor = '#4ade80' }
    else if (state === 'idle') { ignitionText = this.t('ignition_idle'); ignitionColor = '#fbbf24' }
    else { ignitionText = this.t('ignition_off'); ignitionColor = 'var(--mono-text-faint)' }

    const isOnline = vehicle.status === 'online' || vehicle.comm_status === 'ok'
    const commText = isOnline ? this.t('online') : this.t('offline')
    const commColor = isOnline ? '#4ade80' : '#f87171'

    const badge = (dot, text) => `
      <span style="
        display: inline-flex; align-items: center; gap: 4px;
        padding: 2px 7px; background: var(--mono-card-bg); border-radius: 9999px;
        font-size: 10px; font-weight: 500; color: var(--mono-text-secondary);
      ">
        <span style="width: 6px; height: 6px; border-radius: 50%; background: ${dot}; flex-shrink: 0;"></span>
        ${text}
      </span>
    `

    return {
      ignitionBadge: badge(ignitionColor, ignitionText),
      commBadge: badge(commColor, commText)
    }
  }

  formatLastCommunication(gpsDatetime) {
    if (!gpsDatetime) return null
    const diffMs = Date.now() - new Date(gpsDatetime).getTime()
    if (Number.isNaN(diffMs)) return null

    const minutes = Math.floor(diffMs / 60000)
    if (minutes < 1) return 'agora'
    if (minutes < 60) return `há ${minutes} min`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `há ${hours}h`
    const days = Math.floor(hours / 24)
    return `há ${days}d`
  }

  formatDriverName(driver) {
    if (!driver || driver === 'ERROR_OUT_OF_LEN' || driver === '00000' || driver.trim() === '') {
      return this.t('driver_none')
    }
    return driver
  }

  formatRpm(rpm) {
    if (!rpm) return '0'
    const n = parseFloat(rpm)
    return n.toLocaleString('pt-BR')
  }

  formatOdometer(odometer) {
    if (!odometer) return '0'
    const km = parseFloat(odometer)
    if (km >= 1000000) return `${(km / 1000000).toFixed(1)}M`
    if (km >= 1000) return `${(km / 1000).toFixed(1)}k`
    return km.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
  }

  getMetricSvg(icon, color) {
    const svgs = {
      speed: `<svg width="15" height="15" fill="${color}" viewBox="0 0 24 24"><path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.65 6.35,20.04C5.96,20.43 5.35,20.43 4.96,20.04C3.68,18.76 2.78,17.17 2.32,15.45L1.29,16.47C0.9,16.86 0.9,17.47 1.29,17.86C1.68,18.25 2.29,18.25 2.68,17.86L3.7,16.83C5.11,19.37 7.76,21 12,21C18.08,21 23,16.08 23,13C23,6.92 18.08,2 12,2V3Z"/></svg>`,
      rpm: `<svg width="15" height="15" fill="none" stroke="${color}" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path stroke-linecap="round" d="M12 7v5l3 3"/></svg>`,
      temp: `<svg width="15" height="15" fill="${color}" viewBox="0 0 16 16"><path d="M8 14a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"/><path d="M8 0a2.5 2.5 0 00-2.5 2.5v7.55a3.5 3.5 0 105 0V2.5A2.5 2.5 0 008 0z"/></svg>`,
      oil: `<svg width="15" height="15" fill="${color}" viewBox="0 0 24 24"><path d="M19.9 12.66a1 1 0 010 1.41l-5.66 5.66a1 1 0 01-1.41 0l-7.09-7.09a1 1 0 010-1.41L11.4 5.57a1 1 0 011.41 0l7.09 7.09zM3.07 7.93L7.5 3.5 9 5 4.5 9.5 3.07 7.93z"/></svg>`,
      pedal: `<svg width="15" height="15" fill="${color}" viewBox="0 0 24 24"><path d="M8,2H16A2,2 0 0,1 18,4V20A2,2 0 0,1 16,22H8A2,2 0 0,1 6,20V4A2,2 0 0,1 8,2M8,4V20H16V4H8M10,6H14V10H10V6M10,12H14V18H10V12Z"/></svg>`,
      odo: `<svg width="15" height="15" fill="${color}" viewBox="0 0 24 24"><path d="M12,16A3,3 0 0,1 9,13C9,11.88 9.61,10.9 10.5,10.39L20.21,4.77L14.68,14.35C14.18,15.33 13.17,16 12,16M12,3C13.81,3 15.5,3.5 16.97,4.32L14.87,5.53C14,5.19 13,5 12,5A8,8 0 0,0 4,13C4,15.21 4.89,17.21 6.34,18.65H6.35C6.74,19.04 6.74,19.65 6.35,20.04C5.96,20.43 5.35,20.43 4.96,20.04C3.68,18.76 2.78,17.17 2.32,15.45L1.29,16.47C0.9,16.86 0.9,17.47 1.29,17.86C1.68,18.25 2.29,18.25 2.68,17.86L3.7,16.83C5.11,19.37 7.76,21 12,21C18.08,21 23,16.08 23,13C23,6.92 18.08,2 12,2V3Z"/></svg>`,
      hour: `<svg width="15" height="15" fill="${color}" viewBox="0 0 24 24"><path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/></svg>`,
      fuel: `<svg width="15" height="15" fill="${color}" viewBox="0 0 24 24"><path d="M18,10A1,1 0 0,1 17,9A1,1 0 0,1 18,8A1,1 0 0,1 19,9A1,1 0 0,1 18,10M13,18H11V17H8V18H6V7H13V12.09L15,14.08V10H17L19.5,12.5L18,14L16.5,12.5L15,14V18.5A1.5,1.5 0 0,1 13.5,20H11.5A1.5,1.5 0 0,1 10,18.5V18H13M4,2H14A2,2 0 0,1 16,4V8H14V4H4V20H12V22H4A2,2 0 0,1 2,20V4A2,2 0 0,1 4,2Z"/></svg>`
    }
    return svgs[icon] || svgs.speed
  }

  escapeHtml(str) {
    if (!str) return ''
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  }

  isMapRelatedElement(element) {
    const mapSelectors = ['.gmnoprint', '.gm-style', '.gm-control-active', '.gm-bundled-control']
    let el = element
    while (el && el !== document.body) {
      if (mapSelectors.some(s => el.matches && el.matches(s))) return true
      if ((el.tagName === 'svg' || el.tagName === 'path') && el.closest('.gm-style')) return true
      el = el.parentElement
    }
    return false
  }

  destroy() {
    this.closeAllBalloons()
  }
}
